import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  OutlinedInput,
  Stack,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useForm, useController } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { AutocompleteFormInput, SelectFormInput, TextFieldFormInput } from '../formInputs';
import StyledButton from '../buttons/StyledButton';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAlert, useCurrentUser, useDatastore } from '../../hooks';
import { usePackageTypes } from '../../hooks/usePackageType';
import { QM_MAX_ATTACHMENTS, QM_MAX_ATTACHMENT_SIZE_BYTES } from '../../common/constants';
import { QMIssueProps } from '../../datastore/qmIssue';
import { QMReasonProps } from '../../datastore/qmReason';
import { fetchPODetails } from '../../api/poApi';
import { ReceiptLine } from '../../common/types';
import useObserveSsccs from '../../hooks/useSubscribeSsccs';
import { SSCCProps } from '../../datastore/ssccs';
import { QmRecordType } from '../../api/qmCrud';
import {
  QmClosureCodeOption,
  listQmReferenceClosureCodesRequest,
  listQmReferenceLocationsRequest,
  listQmReferenceReasonsRequest
} from '../../api/qmCrud';

interface RaiseQMNewFormProps {
  open: boolean;
  onSubmit: (data: RaiseQMNewModel) => void;
  onClose: () => void;
}

type PoLineOption = {
  name: string;
  value: string;
  poLineId: string;
  itemId: string;
  status: string;
  sourcePartId: string;
  purchaseType: string;
  description: string;
  quantityOrdered: number;
  quantityReceived: number;
};

export interface RaiseQMNewModel {
  type: QmRecordType;
  reason: QMReasonProps;
  issue: QMIssueProps;
  ssccOrAsn: string;
  purchaseOrderNumber: string;
  purchaseOrderLine: string;
  poLine: string;
  poLineId: string;
  itemId: string;
  supplierName: string;
  contactName: string;
  other: string;
  numberOfLabels: number | '';
  packageTypes: string;
  locationName: string;
  progress: string;
  description: string;
  closureCode: string;
  closureNotes: string;
  attachments: File[];
}

const REASONS_ALLOW_MISSING_IDENTIFIER = new Set(['MISSING_PAPERWORK', 'INCORRECT_PO_TO']);

function PackageCountStepper({ control }: { control: ReturnType<typeof useForm<RaiseQMNewModel>>['control'] }) {
  const {
    field: { value, onChange },
    fieldState: { error }
  } = useController({
    name: 'numberOfLabels',
    control,
    rules: {
      validate: (v) => {
        if (!v && v !== 0) return 'Enter Number of Packages';
        return Number(v) >= 1 || 'Must be at least 1';
      }
    }
  });
  const count = Math.max(1, Number(value) || 1);
  const displayValue = !value && value !== 0 ? '' : Number(value);

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" alignItems="center" sx={{ width: 'fit-content' }}>
        <IconButton
          size="small"
          onClick={() => onChange(String(Math.max(1, count - 1)))}
          disabled={count <= 1}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRight: 'none',
            borderRadius: '4px 0 0 4px',
            p: 0.75
          }}>
          <RemoveIcon fontSize="small" />
        </IconButton>
        <OutlinedInput
          size="small"
          value={displayValue}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') {
              onChange('');
            } else {
              const v = parseInt(raw);
              onChange(isNaN(v) ? '' : String(v));
            }
          }}
          inputProps={{ style: { textAlign: 'center', width: 48, padding: '0.45em 0', fontWeight: 600 } }}
          sx={{
            borderRadius: 0
          }}
        />
        <IconButton
          size="small"
          onClick={() => {
            if (!value && value !== 0) {
              onChange('1');
              return;
            }
            onChange(String(count + 1));
          }}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderLeft: 'none',
            borderRadius: '0 4px 4px 0',
            p: 0.75
          }}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Stack>
      {error && (
        <Typography variant="caption" color="error" sx={{ fontSize: '0.68rem' }}>
          {error.message || 'Required'}
        </Typography>
      )}
    </Stack>
  );
}

export default function RaiseQMNewForm({ open, onSubmit, onClose }: RaiseQMNewFormProps) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const alert = useAlert();
  const store = useDatastore();
  const packageTypeOptions = usePackageTypes();
  const user = useCurrentUser();
  const ssccs = useObserveSsccs(0);
  const [qmReasons, setQMReasons] = useState<QMReasonProps[]>([]);
  const [qmIssues, setQMIssues] = useState<QMIssueProps[]>([]);
  const [qmClosureCodes, setQmClosureCodes] = useState<QmClosureCodeOption[]>([]);
  const [qmLocations, setQmLocations] = useState<Array<{ code: string; label: string }>>([]);
  const [isReasonOptionsLoading, setIsReasonOptionsLoading] = useState(true);
  const [isClosureOptionsLoading, setIsClosureOptionsLoading] = useState(true);
  const [isLocationOptionsLoading, setIsLocationOptionsLoading] = useState(true);
  const [isPoSearchLoading, setIsPoSearchLoading] = useState(false);
  const [reasonDisplayOrderByCode, setReasonDisplayOrderByCode] = useState<Record<string, number>>({});
  const [ssccOrAsnInput, setSsccOrAsnInput] = useState('');
  const [purchaseOrderInput, setPurchaseOrderInput] = useState('');
  const [purchaseOrderLineOptions, setPurchaseOrderLineOptions] = useState<PoLineOption[]>([]);
  const [imagePreviews, setImagePreviews] = useState<{ name: string; url: string; file: File }[]>([]);
  const imagePreviewUrlsRef = useRef<string[]>([]);
  const [selectedPreview, setSelectedPreview] = useState<{ name: string; url: string } | null>(null);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const compactSelectSx = {
    width: '100%',
    borderRadius: 1.5,
    '&.MuiInputBase-root': {
      minHeight: '40px'
    },
    '& .MuiSelect-select': {
      minHeight: '40px',
      display: 'flex',
      alignItems: 'center',
      paddingTop: '0px',
      paddingBottom: '0px'
    }
  };

  const sectionCardSx = {
    p: { xs: 1.25, md: 1.5 },
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper'
  };

  const sectionTitleSx = {
    mb: 1,
    fontSize: '0.82rem',
    fontWeight: 600,
    color: 'text.secondary'
  };

  const fieldLabelSx = {
    fontWeight: 500,
    color: 'text.primary',
    lineHeight: 1.25
  };

  const invalidOutlineSx = (isInvalid: boolean) => ({
    borderRadius: 1.5,
    p: 0.5,
    transition: 'box-shadow 0.2s ease',
    ...(isInvalid
      ? {
          boxShadow: `0 0 0 1px ${theme.palette.error.main}`
        }
      : {})
  });

  function formatStatusLabel(rawStatus: string): string {
    const normalized = rawStatus.trim().toLowerCase();

    if (normalized === 'partially_received' || normalized === 'partially-received') {
      return 'Partially Received';
    }

    return rawStatus.replace(/[_-]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  }

  function buildPoLineOption(line: ReceiptLine): PoLineOption | null {
    const poLineNum = String(line.poLineNum || '').trim();
    if (!poLineNum) return null;
    const resolvedPoLineId = String(line.originalPoLineId ?? line.id ?? '').trim();
    const resolvedItemId = String(line.partId ?? '').trim();
    if (!resolvedPoLineId) return null;

    const status = formatStatusLabel(String(line.status || 'N/A').trim());
    const sourcePartId = String(line.sourcePartId || 'N/A').trim();
    const purchaseType = String(line.purchaseType || 'N/A').trim();
    const quantityOrdered = line.quantityOrdered ?? 0;
    const quantityReceived = line.quantityReceived ?? 0;
    const description = String(line.description || 'N/A').trim();
    const generatedPoLineText = `PO Line ${poLineNum} ${sourcePartId} ${purchaseType} ${description}`
      .replace(/\s+/g, ' ')
      .trim();

    return {
      value: generatedPoLineText,
      name: `PO Line ${poLineNum}`,
      poLineId: resolvedPoLineId,
      itemId: resolvedItemId,
      status,
      sourcePartId,
      purchaseType,
      description: description,
      quantityOrdered,
      quantityReceived
    };
  }

  const extractPoLineToken = useCallback((rawValue: string): string => {
    const normalized = String(rawValue || '').trim();
    if (!normalized) return '';

    const poLinePrefixMatch = normalized.match(/^PO\s+Line\s+(.+)$/i);
    if (poLinePrefixMatch?.[1]) {
      return poLinePrefixMatch[1].trim().split(/\s+/)[0] || '';
    }

    return normalized;
  }, []);

  const resolvePoLineOption = useCallback(
    (rawValue: string, fallbackPoLineId?: string): PoLineOption | null => {
      const normalizedValue = String(rawValue || '').trim();
      const normalizedPoLineId = String(fallbackPoLineId || '').trim();

      if (!normalizedValue && !normalizedPoLineId) return null;

      if (normalizedPoLineId) {
        const byId = purchaseOrderLineOptions.find((option) => option.poLineId === normalizedPoLineId);
        if (byId) return byId;
      }

      const directMatch = purchaseOrderLineOptions.find(
        (option) => option.value === normalizedValue || option.name === normalizedValue
      );
      if (directMatch) return directMatch;

      const token = extractPoLineToken(normalizedValue);
      if (!token) return null;

      return (
        purchaseOrderLineOptions.find((option) => {
          const optionToken = extractPoLineToken(option.name);
          return optionToken.toLowerCase() === token.toLowerCase();
        }) || null
      );
    },
    [extractPoLineToken, purchaseOrderLineOptions]
  );

  const defaultValues: RaiseQMNewModel = {
    type: 'INBOUND',
    reason: { id: '', reasonId: '', name: '' },
    issue: { id: '', issueId: '', name: '' },
    ssccOrAsn: '',
    purchaseOrderNumber: '',
    purchaseOrderLine: '',
    poLine: '',
    poLineId: '',
    itemId: '',
    supplierName: '',
    contactName: '',
    other: '',
    numberOfLabels: '',
    packageTypes: '',
    locationName: '',
    progress: '',
    description: '',
    closureCode: '',
    closureNotes: '',
    attachments: []
  };

  const {
    handleSubmit,
    control,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors }
  } = useForm<RaiseQMNewModel>({ defaultValues });

  const currentSupplierName = String(watch('supplierName') || '').trim();
  const currentContactName = String(watch('contactName') || '').trim();
  const currentPackageTypes = String(watch('packageTypes') || '').trim();
  const currentNumberOfLabels = watch('numberOfLabels');
  const currentSsccOrAsn = String(watch('ssccOrAsn') || '').trim();
  const currentPurchaseOrderNumber = String(watch('purchaseOrderNumber') || '').trim();
  const selectedReasonCode = String(watch('reason.reasonId') || '').trim();
  const selectedClosureCode = String(watch('closureCode') || '').trim();
  const selectedPoLine = String(watch('purchaseOrderLine') || '').trim();
  const selectedLocationName = String(watch('locationName') || '').trim();
  const isPoProvided = currentPurchaseOrderNumber.length > 0;
  const allowsMissingIdentifier = REASONS_ALLOW_MISSING_IDENTIFIER.has(selectedReasonCode);
  const requiresIdentifier = !allowsMissingIdentifier;
  const hasSupplierOrContact = !!currentSupplierName || !!currentContactName;

  const isSupplierRequired = allowsMissingIdentifier ? !currentContactName : !isPoProvided && !currentContactName;
  const isContactRequired = allowsMissingIdentifier ? !currentSupplierName : !isPoProvided || !currentSupplierName;

  const selectedReasonDisplayOrder = reasonDisplayOrderByCode[selectedReasonCode] ?? null;
  const shouldShowClosureSection = selectedReasonDisplayOrder !== null && selectedReasonDisplayOrder <= 4;
  const isClosureSelectionRequired = selectedReasonDisplayOrder !== null && selectedReasonDisplayOrder <= 2;
  const hasClosureOptionsForReason = qmClosureCodes.length > 0;

  const missingRequiredItems = useMemo(() => {
    const missing: string[] = [];

    if (!selectedReasonCode) missing.push('Reason For QM');
    if (requiresIdentifier && !currentSsccOrAsn && !currentPurchaseOrderNumber) {
      missing.push('SSCC / ASN or Purchase Order');
    }
    if (allowsMissingIdentifier && !hasSupplierOrContact) {
      missing.push('Supplier or Contact');
    }
    if (isSupplierRequired && !currentSupplierName) missing.push('Supplier');
    if (isContactRequired && !currentContactName) missing.push('Contact');
    if (!currentNumberOfLabels && currentNumberOfLabels !== 0) missing.push('Number of Packages');
    if (!currentPackageTypes) missing.push('Package Types');
    if (!selectedLocationName) missing.push('Item Location');
    if (isClosureSelectionRequired && hasClosureOptionsForReason && !selectedClosureCode) {
      missing.push('Closure Code');
    }

    return missing;
  }, [
    selectedReasonCode,
    requiresIdentifier,
    allowsMissingIdentifier,
    hasSupplierOrContact,
    currentSsccOrAsn,
    currentPurchaseOrderNumber,
    isSupplierRequired,
    currentSupplierName,
    isContactRequired,
    currentContactName,
    currentNumberOfLabels,
    currentPackageTypes,
    selectedLocationName,
    isClosureSelectionRequired,
    hasClosureOptionsForReason,
    selectedClosureCode
  ]);

  useEffect(() => {
    async function getData() {
      try {
        const [reasons, locations] = await Promise.all([
          listQmReferenceReasonsRequest(),
          listQmReferenceLocationsRequest()
        ]);
        const issues = await store.qmIssues.getArray();
        if (reasons) {
          setReasonDisplayOrderByCode(Object.fromEntries(reasons.map((reason) => [reason.code, reason.displayOrder])));
          setQMReasons(
            reasons.map((reason) => ({
              id: reason.code,
              reasonId: reason.code,
              name: reason.label
            }))
          );
        }
        if (locations) setQmLocations(locations.map((location) => ({ code: location.code, label: location.label })));
        if (issues) setQMIssues(issues);
      } finally {
        setIsReasonOptionsLoading(false);
        setIsLocationOptionsLoading(false);
      }
    }
    getData();
  }, [store]);

  useEffect(() => {
    let active = true;

    async function loadClosureCodes() {
      setIsClosureOptionsLoading(true);
      try {
        const closureCodes = await listQmReferenceClosureCodesRequest(selectedReasonCode || null);
        if (active) {
          setQmClosureCodes(closureCodes || []);
        }
      } finally {
        if (active) {
          setIsClosureOptionsLoading(false);
        }
      }
    }

    loadClosureCodes();

    return () => {
      active = false;
    };
  }, [selectedReasonCode]);

  useEffect(() => {
    if (!selectedPoLine) {
      setValue('poLineId', '');
      setValue('itemId', '');
      return;
    }

    const matchedPoLine = resolvePoLineOption(selectedPoLine);

    if (!matchedPoLine) {
      setValue('poLineId', '');
      setValue('itemId', '');
      return;
    }

    if (selectedPoLine !== matchedPoLine.value) {
      setValue('purchaseOrderLine', matchedPoLine.value);
    }
    setValue('poLineId', matchedPoLine.poLineId || '');
    setValue('itemId', matchedPoLine.itemId || '');
  }, [purchaseOrderLineOptions, resolvePoLineOption, selectedPoLine, setValue]);

  useEffect(() => {
    if (!requiresIdentifier || currentSsccOrAsn || currentPurchaseOrderNumber) {
      clearErrors(['ssccOrAsn', 'purchaseOrderNumber']);
    }
  }, [requiresIdentifier, currentSsccOrAsn, currentPurchaseOrderNumber, clearErrors]);

  useEffect(() => {
    if (allowsMissingIdentifier && hasSupplierOrContact) {
      clearErrors(['supplierName', 'contactName']);
    }
  }, [allowsMissingIdentifier, hasSupplierOrContact, clearErrors]);

  useEffect(() => {
    if (!shouldShowClosureSection) {
      setValue('closureCode', '');
      setValue('closureNotes', '');
    }
  }, [setValue, shouldShowClosureSection]);

  useEffect(() => {
    if (!selectedClosureCode) return;

    const isValidClosureCode = qmClosureCodes.some((item) => item.code === selectedClosureCode);
    if (!isValidClosureCode) {
      setValue('closureCode', '');
    }
  }, [qmClosureCodes, selectedClosureCode, setValue]);

  useEffect(() => {
    imagePreviewUrlsRef.current = imagePreviews.map((image) => image.url);
  }, [imagePreviews]);

  useEffect(() => {
    return () => {
      imagePreviewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  function onPictureUpload(files: FileList | null) {
    if (!files || files.length === 0) return;

    const incomingFiles = Array.from(files);
    const imageFiles = incomingFiles.filter((file) => file.type.startsWith('image/'));

    if (imageFiles.length !== incomingFiles.length) {
      alert('Only image files can be attached.', 'warning');
    }

    const validSizeFiles = imageFiles.filter((file) => file.size <= QM_MAX_ATTACHMENT_SIZE_BYTES);
    if (validSizeFiles.length !== imageFiles.length) {
      const maxSizeInMb = Math.round((QM_MAX_ATTACHMENT_SIZE_BYTES / (1024 * 1024)) * 10) / 10;
      alert(`Some files were skipped because they exceed ${maxSizeInMb}MB.`, 'warning');
    }

    const availableSlots = Math.max(0, QM_MAX_ATTACHMENTS - imagePreviews.length);
    if (availableSlots === 0) {
      alert(`Maximum ${QM_MAX_ATTACHMENTS} attachments reached.`, 'warning');
      return;
    }

    const acceptedFiles = validSizeFiles.slice(0, availableSlots);
    if (acceptedFiles.length < validSizeFiles.length) {
      alert(`Only the first ${availableSlots} file(s) were added to stay within the attachment limit.`, 'warning');
    }

    const newPreviews = acceptedFiles.map((file) => ({
      name: file.name,
      url: URL.createObjectURL(file),
      file
    }));

    if (newPreviews.length === 0) return;

    setImagePreviews((previous) => {
      const combined = [...previous, ...newPreviews];
      setValue(
        'attachments',
        combined.map((preview) => preview.file)
      );
      return combined;
    });
  }

  function onDeletePicture(imageUrl: string) {
    setImagePreviews((previous) => {
      const target = previous.find((image) => image.url === imageUrl);
      if (target) {
        URL.revokeObjectURL(target.url);
      }
      if (selectedPreview?.url === imageUrl) {
        setSelectedPreview(null);
      }
      const next = previous.filter((image) => image.url !== imageUrl);
      setValue(
        'attachments',
        next.map((image) => image.file)
      );
      return next;
    });
  }

  function clearPoDependentFields() {
    setPurchaseOrderLineOptions([]);
    setValue('supplierName', '');
    setValue('contactName', '');
    setValue('purchaseOrderLine', '');
    setValue('poLineId', '');
    setValue('itemId', '');
  }

  async function runPoSearch(purchaseOrderValue: string) {
    setIsPoSearchLoading(true);
    const purchaseOrder = purchaseOrderValue.trim();

    try {
      if (!purchaseOrder) {
        clearPoDependentFields();
        return;
      }

      const poResponse = await fetchPODetails(purchaseOrder, '');
      if (poResponse.error || !poResponse.payload) {
        alert('No data found for the entered Purchase Order', 'warning');
        clearPoDependentFields();
        return;
      }

      const supplier = String(poResponse.payload.supplierName || '').trim();
      const contact = String(poResponse.payload.shipToAttention || '').trim();
      setValue('supplierName', supplier);
      setValue('contactName', contact);
      if (supplier) {
        clearErrors('supplierName');
      }
      if (contact) {
        clearErrors('contactName');
      }

      const poLines = (poResponse.payload.lines || [])
        .map((line: ReceiptLine) => buildPoLineOption(line))
        .filter((line): line is PoLineOption => !!line);

      const dedupedPoLines = poLines.filter(
        (line, index, arr) =>
          arr.findIndex((current) => current.name === line.name && current.value === line.value) === index
      );

      if (!dedupedPoLines.length) {
        alert('No PO found for the entered Purchase Order', 'warning');
      }

      setPurchaseOrderLineOptions(dedupedPoLines);
      setValue('purchaseOrderLine', '');
    } finally {
      setIsPoSearchLoading(false);
    }
  }

  async function onSearchPurchaseOrder() {
    await runPoSearch(purchaseOrderInput);
  }

  function prefillPackageFieldsFromSscc(sscc: SSCCProps, asnValue?: string) {
    const normalizedAsn = String(asnValue || '')
      .trim()
      .toUpperCase();

    const packages = (sscc.receiptedEvents || [])
      .flatMap((event) => event.packages || [])
      .filter((pkg) => {
        if (!normalizedAsn) return true;
        return (
          String(pkg.asn || '')
            .trim()
            .toUpperCase() === normalizedAsn
        );
      });

    const packageCount = packages.reduce((total, pkg) => total + Number(pkg.quantity || 0), 0);
    const packageTypes = Array.from(
      new Set(packages.map((pkg) => String(pkg.packageType || '').trim()).filter((pkgType) => !!pkgType))
    ).join(', ');

    if (packageCount > 0) {
      setValue('numberOfLabels', packageCount);
    }

    if (packageTypes) {
      setValue('packageTypes', packageTypes);
    }
  }

  async function onSearchSsccOrAsn() {
    const lookup = ssccOrAsnInput.trim();
    if (!lookup) {
      setError('ssccOrAsn', { type: 'manual', message: 'Enter SSCC/ASN to search' });
      return;
    }

    const isAsn = /^MRL[A-Za-z0-9]{8}$/i.test(lookup);
    const isSscc = /^[A-Za-z0-9]{15}$/.test(lookup);

    if (!isAsn && !isSscc) {
      setError('ssccOrAsn', {
        type: 'manual',
        message: 'Invalid format. Use SSCC (15 chars) or ASN (MRL + 8 characters)'
      });
      return;
    }

    let resolvedPo = '';

    if (isSscc) {
      const matchedSscc = ssccs.find((sscc) => sscc.id.toLowerCase() === lookup.toLowerCase());
      if (matchedSscc) {
        prefillPackageFieldsFromSscc(matchedSscc);
      }
      resolvedPo =
        matchedSscc?.receiptedEvents
          ?.flatMap((event) => event.packages || [])
          .map((pkg) => String(pkg.po || '').trim())
          .find((po) => !!po) || '';
    }

    if (isAsn) {
      const matched = ssccs.find((sscc) =>
        (sscc.receiptedEvents || []).some((event) =>
          (event.packages || []).some(
            (pkg) =>
              String(pkg.asn || '')
                .trim()
                .toUpperCase() === lookup.toUpperCase()
          )
        )
      );

      if (matched) {
        prefillPackageFieldsFromSscc(matched, lookup);
        resolvedPo =
          matched.receiptedEvents
            ?.flatMap((event) => event.packages || [])
            .filter(
              (pkg) =>
                String(pkg.asn || '')
                  .trim()
                  .toUpperCase() === lookup.toUpperCase()
            )
            .map((pkg) => String(pkg.po || '').trim())
            .find((po) => !!po) || '';
      }
    }

    if (!resolvedPo) {
      setError('ssccOrAsn', { type: 'manual', message: 'No data found for the entered SSCC/ASN' });
      alert('No data found for the entered SSCC/ASN', 'warning');
      setPurchaseOrderInput('');
      setValue('purchaseOrderNumber', '');
      clearPoDependentFields();
      return;
    }

    clearErrors('ssccOrAsn');
    setPurchaseOrderInput(resolvedPo);
    setValue('purchaseOrderNumber', resolvedPo);
    await runPoSearch(resolvedPo);
  }

  function onSubmitForm(data: RaiseQMNewModel) {
    setHasAttemptedSubmit(true);
    clearErrors();

    applyDynamicRequiredErrors();

    if (missingRequiredItems.length > 0) {
      return;
    }

    data.type = 'INBOUND';
    const normalizedPoLine = String(data.purchaseOrderLine || '').trim();
    const selectedPoLineOption = resolvePoLineOption(normalizedPoLine, data.poLineId);
    data.purchaseOrderLine = selectedPoLineOption?.value || normalizedPoLine;
    data.poLineId = selectedPoLineOption?.poLineId || String(data.poLineId || '').trim();
    data.itemId = selectedPoLineOption?.itemId || String(data.itemId || '').trim();
    data.reason = qmReasons.find((r) => r.reasonId === data.reason.reasonId) || { id: '', reasonId: '', name: '' };
    data.issue = qmIssues.find((i) => i.issueId === data.issue.issueId) || { id: '', issueId: '', name: '' };

    if (!shouldShowClosureSection || !data.closureCode) {
      data.closureCode = '';
      data.closureNotes = '';
    }

    onSubmit(data);
  }

  function applyDynamicRequiredErrors() {
    if (missingRequiredItems.includes('Reason For QM')) {
      setError('reason.reasonId', { type: 'manual', message: 'Select Reason for QM' });
    }
    if (missingRequiredItems.includes('SSCC / ASN or Purchase Order')) {
      setError('ssccOrAsn', { type: 'manual', message: 'Provide SSCC/ASN or Purchase Order' });
      setError('purchaseOrderNumber', { type: 'manual', message: 'Provide Purchase Order or SSCC/ASN' });
    }
    if (missingRequiredItems.includes('Supplier or Contact')) {
      setError('supplierName', { type: 'manual', message: 'Provide Supplier or Contact' });
      setError('contactName', { type: 'manual', message: 'Provide Supplier or Contact' });
    }
    if (missingRequiredItems.includes('Supplier')) {
      setError('supplierName', { type: 'manual', message: 'Provide Supplier or Provide Contact' });
    }
    if (missingRequiredItems.includes('Contact')) {
      setError('contactName', { type: 'manual', message: 'Provide Supplier or Provide Contact' });
    }
    if (missingRequiredItems.includes('Number of Packages')) {
      setError('numberOfLabels', { type: 'manual', message: 'Enter Number of Packages' });
    }
    if (missingRequiredItems.includes('Package Types')) {
      setError('packageTypes', { type: 'manual', message: 'Provide Package Types' });
    }
    if (missingRequiredItems.includes('Item Location')) {
      setError('locationName', { type: 'manual', message: 'Select Item Location' });
    }
    if (missingRequiredItems.includes('Closure Code')) {
      setError('closureCode', { type: 'manual', message: 'Select Closure Code' });
    }
  }

  function onInvalidSubmit() {
    setHasAttemptedSubmit(true);
    applyDynamicRequiredErrors();
  }

  return (
    <>
      <Dialog
        open={open}
        maxWidth="lg"
        fullWidth
        fullScreen={isSmallScreen}
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, md: 2 },
            overflow: 'hidden',
            maxHeight: { xs: '100dvh', md: '92dvh' },
            bgcolor: 'background.default',
            backgroundImage: 'none',
            display: 'flex'
          }
        }}>
        <form
          onSubmit={handleSubmit(onSubmitForm, onInvalidSubmit)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            height: '100%'
          }}>
          <Stack
            spacing={2}
            sx={{
              minHeight: 0,
              flex: 1,
              overflowY: 'auto',
              p: { xs: 1.25, md: '1rem 2rem' },
              '& .MuiTypography-root': { fontSize: { xs: '0.84rem', md: '0.88rem' } },
              '& .MuiButton-root': { fontSize: { xs: '0.76rem', md: '0.8rem' } },
              '& .MuiInputBase-input': { fontSize: { xs: '0.84rem', md: '0.88rem' } },
              '& .MuiFormHelperText-root': { fontSize: '0.68rem', lineHeight: 1.2 }
            }}>
            <Stack spacing={1} sx={{ pt: 0.25 }}>
              <Box
                sx={{
                  px: { xs: 1, md: 1.5 },
                  py: { xs: 1, md: 1.25 },
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper'
                }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.25}>
                  <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                      <Typography
                        variant="h2"
                        fontWeight={700}
                        sx={{
                          lineHeight: 1.1,
                          mr: 0.5,
                          fontSize: { xs: '0.98rem !important', md: '1.12rem !important' }
                        }}>
                        Raise a QM
                      </Typography>
                      <Chip size="small" color="primary" variant="filled" label="New" />
                      <Chip size="small" variant="outlined" label="INBOUND" />
                    </Stack>
                  </Stack>
                  <IconButton
                    onClick={onClose}
                    sx={{
                      mt: -0.25,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': { bgcolor: 'grey.100' }
                    }}>
                    <CloseIcon />
                  </IconButton>
                </Stack>
              </Box>
            </Stack>
            <Divider sx={{ opacity: 0.7 }} />
            <Stack spacing={2}>
              <Box sx={sectionCardSx}>
                <Typography sx={sectionTitleSx}>Identification</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between">
                  <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={fieldLabelSx}>Reason For QM</Typography>
                    {isReasonOptionsLoading ? (
                      <Button variant="outlined" disabled sx={{ justifyContent: 'flex-start', minHeight: '40px' }}>
                        Loading reason options...
                      </Button>
                    ) : (
                      <Box sx={invalidOutlineSx(!!errors.reason?.reasonId)}>
                        <SelectFormInput
                          sx={compactSelectSx}
                          name="reason.reasonId"
                          required
                          control={control}
                          error={errors.reason?.reasonId as any}
                          options={qmReasons.map((type) => ({ name: type.name, value: type.reasonId }))}
                        />
                      </Box>
                    )}
                  </Stack>
                  <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={fieldLabelSx}>SSCC / ASN</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ ...invalidOutlineSx(!!errors['ssccOrAsn']), flex: 1 }}>
                        <TextFieldFormInput
                          sx={{ width: '100%' }}
                          name="ssccOrAsn"
                          control={control}
                          error={errors['ssccOrAsn']}
                          onChange={(value) => setSsccOrAsnInput(value)}
                        />
                      </Box>
                      <Button variant="outlined" onClick={onSearchSsccOrAsn}>
                        Search
                      </Button>
                    </Stack>
                  </Stack>
                  <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={fieldLabelSx}>Purchase Order</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ ...invalidOutlineSx(!!errors['purchaseOrderNumber']), flex: 1 }}>
                        <TextFieldFormInput
                          sx={{ width: '100%' }}
                          name="purchaseOrderNumber"
                          control={control}
                          error={errors['purchaseOrderNumber']}
                          onChange={(value) => {
                            setPurchaseOrderInput(value);
                            clearPoDependentFields();
                          }}
                        />
                      </Box>
                      <Button
                        variant="outlined"
                        onClick={onSearchPurchaseOrder}
                        disabled={isPoSearchLoading}
                        sx={{ minWidth: 88 }}>
                        {isPoSearchLoading ? <CircularProgress size={16} /> : 'Search'}
                      </Button>
                    </Stack>
                  </Stack>
                </Stack>
              </Box>

              <Box sx={sectionCardSx}>
                <Typography sx={sectionTitleSx}>Purchase Order Details</Typography>
                <Stack spacing={1} sx={{ width: '100%' }}>
                  <Typography sx={fieldLabelSx}>Purchase Order Line</Typography>
                  {purchaseOrderInput.trim() && purchaseOrderLineOptions.length > 0 ? (
                    <AutocompleteFormInput
                      sx={{ width: '100%' }}
                      name="purchaseOrderLine"
                      control={control}
                      error={errors['purchaseOrderLine']}
                      placeholder="Search by PO line, part, type, or description"
                      options={purchaseOrderLineOptions}
                      getOptionLabel={(option) => option.value}
                      renderOptionContent={(option) => (
                        <Box sx={{ width: '100%', py: 0.5 }}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            sx={{ mb: 0.75 }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                              {option.name}
                            </Typography>
                            <Stack direction="row" spacing={0.5} sx={{ ml: 1, flexShrink: 0 }}>
                              {option.status && (
                                <Chip
                                  size="small"
                                  label={option.status}
                                  color="info"
                                  variant="filled"
                                  sx={{ height: 20, fontSize: '0.65rem' }}
                                />
                              )}
                              {option.purchaseType && (
                                <Chip
                                  size="small"
                                  label={option.purchaseType}
                                  color="default"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.65rem' }}
                                />
                              )}
                            </Stack>
                          </Stack>
                          {option.sourcePartId && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                              Part: <strong>{option.sourcePartId}</strong>
                            </Typography>
                          )}
                          {option.description && (
                            <Typography
                              variant="body2"
                              color="text.primary"
                              sx={{ mb: 0.5, fontSize: '0.85rem', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {option.description}
                            </Typography>
                          )}
                          <Stack direction="row" spacing={2}>
                            <Typography variant="caption" color="text.secondary">
                              Ordered: <strong>{option.quantityOrdered ?? '—'}</strong>
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Received: <strong>{option.quantityReceived ?? '—'}</strong>
                            </Typography>
                          </Stack>
                        </Box>
                      )}
                    />
                  ) : (
                    <TextFieldFormInput
                      sx={{ width: '100%' }}
                      name="purchaseOrderLine"
                      control={control}
                      error={errors['purchaseOrderLine']}
                    />
                  )}
                </Stack>
              </Box>

              <Box sx={sectionCardSx}>
                <Typography sx={sectionTitleSx}>Contact and Package</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between">
                  <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={fieldLabelSx}>Supplier</Typography>
                    <Box sx={invalidOutlineSx(!!errors['supplierName'])}>
                      <TextFieldFormInput
                        sx={{ width: '100%' }}
                        name="supplierName"
                        control={control}
                        required={isSupplierRequired}
                        error={errors['supplierName']}
                      />
                    </Box>
                  </Stack>
                  <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={fieldLabelSx}>Contact</Typography>
                    <Box sx={invalidOutlineSx(!!errors['contactName'])}>
                      <TextFieldFormInput
                        sx={{ width: '100%' }}
                        name="contactName"
                        control={control}
                        required={isContactRequired}
                        error={errors['contactName']}
                      />
                    </Box>
                  </Stack>
                  <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={fieldLabelSx}>Additional Details</Typography>
                    <TextFieldFormInput sx={{ width: '100%' }} name="other" control={control} error={errors['other']} />
                  </Stack>
                </Stack>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.5}
                  justifyContent="space-between"
                  sx={{ mt: 1.5 }}>
                  <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={fieldLabelSx}>Item Location</Typography>
                    {isLocationOptionsLoading ? (
                      <Button variant="outlined" disabled sx={{ justifyContent: 'flex-start', minHeight: '40px' }}>
                        Loading location options...
                      </Button>
                    ) : (
                      <Box sx={invalidOutlineSx(!!errors['locationName'])}>
                        <SelectFormInput
                          sx={compactSelectSx}
                          name="locationName"
                          required
                          control={control}
                          error={errors['locationName']}
                          options={qmLocations.map((location) => ({
                            name: location.label,
                            value: location.code
                          }))}
                        />
                      </Box>
                    )}
                  </Stack>
                  <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={fieldLabelSx}>Number of Packages</Typography>
                    <Box sx={invalidOutlineSx(!!errors['numberOfLabels'])}>
                      <PackageCountStepper control={control} />
                    </Box>
                  </Stack>
                  <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={fieldLabelSx}>Package Types</Typography>
                    <Box sx={invalidOutlineSx(!!errors['packageTypes'])}>
                      <AutocompleteFormInput
                        sx={{ width: '100%' }}
                        name="packageTypes"
                        control={control}
                        required
                        placeholder="Search or select..."
                        options={
                          packageTypeOptions?.map((type) => ({
                            name: type,
                            value: type
                          })) || [{ name: 'Carton', value: 'Carton' }]
                        }
                        error={errors['packageTypes']}
                      />
                    </Box>
                  </Stack>
                </Stack>
              </Box>

              <Box sx={sectionCardSx}>
                <Typography sx={sectionTitleSx}>Description and Attachments</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between">
                  <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={fieldLabelSx}>Description / Comments</Typography>
                    <TextFieldFormInput
                      sx={{
                        '& .MuiInputBase-inputMultiline': {
                          p: '0.45em 0.65em'
                        }
                      }}
                      name="description"
                      control={control}
                      error={errors['description']}
                      multiline
                      rows={3}
                    />
                  </Stack>
                  <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography sx={fieldLabelSx}>Picture Upload</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {imagePreviews.length}/{QM_MAX_ATTACHMENTS} selected
                      </Typography>
                    </Stack>
                    <Button variant="outlined" component="label">
                      Add Picture
                      <input
                        hidden
                        accept="image/*"
                        type="file"
                        multiple
                        onChange={(e) => onPictureUpload(e.target.files)}
                      />
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      Max {QM_MAX_ATTACHMENTS} images, up to{' '}
                      {Math.round((QM_MAX_ATTACHMENT_SIZE_BYTES / (1024 * 1024)) * 10) / 10}MB each.
                    </Typography>
                    {imagePreviews.length > 0 && (
                      <Stack direction="row" spacing={1.5} sx={{ overflowX: 'auto', py: 1 }}>
                        {imagePreviews.map((image) => (
                          <Stack
                            key={image.url}
                            spacing={0.5}
                            sx={{
                              minWidth: 110,
                              maxWidth: 130,
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              overflow: 'hidden',
                              bgcolor: 'background.paper',
                              flexShrink: 0
                            }}>
                            <Box
                              sx={{ position: 'relative', cursor: 'zoom-in' }}
                              onClick={() => setSelectedPreview({ name: image.name, url: image.url })}>
                              <Box
                                component="img"
                                src={image.url}
                                alt={image.name}
                                sx={{
                                  width: '100%',
                                  height: 90,
                                  objectFit: 'cover',
                                  display: 'block'
                                }}
                              />
                              <IconButton
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onDeletePicture(image.url);
                                }}
                                sx={{
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                  bgcolor: 'rgba(0,0,0,0.55)',
                                  color: 'common.white',
                                  p: 0.25,
                                  '&:hover': { bgcolor: 'error.main' }
                                }}>
                                <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Box>
                            <Stack sx={{ px: 0.75, pb: 0.75 }} spacing={0.25}>
                              <Typography
                                variant="caption"
                                noWrap
                                title={image.name}
                                sx={{ fontWeight: 500, color: 'text.primary', fontSize: '0.7rem' }}>
                                {image.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: 'primary.main', fontSize: '0.5rem', fontWeight: 600 }}>
                                {image.file.size < 1024 * 1024
                                  ? `${(image.file.size / 1024).toFixed(1)} KB`
                                  : `${(image.file.size / (1024 * 1024)).toFixed(1)} MB`}
                              </Typography>
                            </Stack>
                          </Stack>
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              </Box>

              {shouldShowClosureSection && (
                <Box sx={sectionCardSx}>
                  <Typography sx={sectionTitleSx}>Closure</Typography>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between">
                    <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                      <Typography>
                        {isClosureSelectionRequired
                          ? 'Closure required for this reason'
                          : 'Optional closure for this reason'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {isClosureSelectionRequired
                          ? 'Select a closure code to close this QM at creation time.'
                          : 'Leave closure blank to send this QM to the coordinator, or select a closure code to close it now.'}
                      </Typography>
                    </Stack>
                    <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={fieldLabelSx}>Closure Code</Typography>
                      {isClosureOptionsLoading ? (
                        <Button variant="outlined" disabled sx={{ justifyContent: 'flex-start', minHeight: '40px' }}>
                          Loading closure options...
                        </Button>
                      ) : (
                        <Box sx={invalidOutlineSx(!!errors['closureCode'])}>
                          <SelectFormInput
                            sx={compactSelectSx}
                            name="closureCode"
                            control={control}
                            required={isClosureSelectionRequired && hasClosureOptionsForReason}
                            error={errors['closureCode'] as any}
                            options={qmClosureCodes.map((item) => ({
                              name: item.label,
                              value: item.code
                            }))}
                          />
                        </Box>
                      )}
                      {!isClosureOptionsLoading && !hasClosureOptionsForReason && (
                        <Typography variant="caption" color="text.secondary">
                          No closure codes configured for the selected reason.
                        </Typography>
                      )}
                    </Stack>
                    <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={fieldLabelSx}>Closure Notes</Typography>
                      <TextFieldFormInput
                        sx={{ width: '100%' }}
                        name="closureNotes"
                        control={control}
                        error={errors['closureNotes'] as any}
                      />
                    </Stack>
                  </Stack>
                </Box>
              )}
            </Stack>
            {hasAttemptedSubmit && missingRequiredItems.length > 0 && (
              <Alert severity="warning">Please complete the highlighted fields.</Alert>
            )}
            <Divider />
            <Stack
              direction={{ xs: 'column-reverse', sm: 'row' }}
              spacing={1.25}
              justifyContent="flex-end"
              sx={{ pb: { xs: 0.5, md: 0.25 } }}>
              <Button variant="text" onClick={onClose} sx={{ minWidth: { sm: 100 } }}>
                Cancel
              </Button>
              <StyledButton variant="contained" type="submit" disabled={user.isReadOnly} sx={{ minWidth: { sm: 140 } }}>
                Raise QM
              </StyledButton>
            </Stack>
          </Stack>
        </form>
      </Dialog>
      <Dialog
        open={!!selectedPreview}
        onClose={() => setSelectedPreview(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.default'
          }
        }}>
        <Stack spacing={1} sx={{ p: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2" noWrap title={selectedPreview?.name || ''}>
              {selectedPreview?.name || ''}
            </Typography>
            <IconButton onClick={() => setSelectedPreview(null)}>
              <CloseIcon />
            </IconButton>
          </Stack>
          {selectedPreview && (
            <Box
              component="img"
              src={selectedPreview.url}
              alt={selectedPreview.name}
              sx={{
                width: '100%',
                maxHeight: { xs: '70vh', md: '78vh' },
                objectFit: 'contain',
                borderRadius: 1
              }}
            />
          )}
        </Stack>
      </Dialog>
    </>
  );
}
